export type GoogleMapConstructorOptions = {
  center: { lat: number; lng: number };
  zoom: number;
  mapId?: string;
  disableDefaultUI?: boolean;
  fullscreenControl?: boolean;
  mapTypeControl?: boolean;
  streetViewControl?: boolean;
  zoomControl?: boolean;
  draggableCursor?: string;
  draggingCursor?: string;
  styles?: GoogleMapStyle[];
};

export type GoogleMapStyle = {
  featureType?: string;
  elementType?: string;
  stylers: Array<Record<string, number | string | boolean>>;
};

export type GoogleLatLngLiteral = {
  lat: number;
  lng: number;
};

export type GoogleLatLng = {
  lat: () => number;
  lng: () => number;
};

export type GoogleMapMouseEvent = {
  latLng: GoogleLatLng | null;
};

export type GoogleMapsListener = {
  remove: () => void;
};

export type GoogleMapInstance = {
  setCenter: (center: GoogleLatLngLiteral) => void;
  setZoom: (zoom: number) => void;
  addListener: (
    eventName: string,
    handler: (event: GoogleMapMouseEvent) => void,
  ) => GoogleMapsListener;
};

export type GoogleMVCArray<TValue> = {
  getLength: () => number;
  getAt: (index: number) => TValue;
  addListener: (
    eventName: string,
    handler: (index: number) => void,
  ) => GoogleMapsListener;
};

export type GoogleMarkerInstance = {
  setMap: (map: object | null) => void;
  setPosition: (position: GoogleLatLngLiteral) => void;
  getPosition?: () => GoogleLatLng | null;
  addListener: (eventName: string, handler: () => void) => GoogleMapsListener;
};

export type GoogleAdvancedMarkerInstance = {
  map: object | null;
  position: GoogleLatLngLiteral | GoogleLatLng;
  title?: string;
  addListener?: (eventName: string, handler: () => void) => GoogleMapsListener;
};

export type GooglePolygonConstructorOptions = {
  map: object;
  paths: GoogleLatLngLiteral[];
  editable?: boolean;
  draggable?: boolean;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  fillColor?: string;
  fillOpacity?: number;
  clickable?: boolean;
};

export type GooglePolygonInstance = {
  setMap: (map: object | null) => void;
  getPath: () => GoogleMVCArray<GoogleLatLng>;
  addListener: (eventName: string, handler: () => void) => GoogleMapsListener;
};

export type GoogleGeocoderResult = {
  formatted_address: string;
  address_components?: GoogleAddressComponent[];
  geometry: {
    location: GoogleLatLng;
  };
};

export type GoogleAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

export type GoogleGeocoder = {
  geocode: (
    request: {
      address?: string;
      placeId?: string;
      location?: GoogleLatLngLiteral;
    },
    callback: (results: GoogleGeocoderResult[] | null, status: string) => void,
  ) => void;
};

export type GooglePlacePrediction = {
  description: string;
  place_id: string;
};

export type GoogleAutocompleteService = {
  getPlacePredictions: (
    request: {
      input: string;
      componentRestrictions?: { country: string | string[] };
    },
    callback: (
      predictions: GooglePlacePrediction[] | null,
      status: string,
    ) => void,
  ) => void;
};

export type GoogleMarkerConstructorOptions = {
  map: object;
  position: { lat: number; lng: number };
  title?: string;
  icon?: string;
  draggable?: boolean;
  zIndex?: number;
};

export type GoogleAdvancedMarkerConstructorOptions = {
  map: object;
  position: GoogleLatLngLiteral;
  title?: string;
  content?: HTMLElement;
  draggable?: boolean;
};

export type GoogleMapsNamespace = {
  maps: {
    importLibrary?: (
      libraryName: 'maps' | 'marker' | 'places',
    ) => Promise<Record<string, unknown>>;
    Map: new (
      container: HTMLElement,
      options: GoogleMapConstructorOptions,
    ) => GoogleMapInstance;
    Marker: new (
      options: GoogleMarkerConstructorOptions,
    ) => GoogleMarkerInstance;
    marker?: {
      AdvancedMarkerElement: new (
        options: GoogleAdvancedMarkerConstructorOptions,
      ) => GoogleAdvancedMarkerInstance;
    };
    Polygon: new (
      options: GooglePolygonConstructorOptions,
    ) => GooglePolygonInstance;
    Geocoder: new () => GoogleGeocoder;
    places: {
      AutocompleteService: new () => GoogleAutocompleteService;
    };
    event: {
      clearInstanceListeners: (instance: object) => void;
      trigger: (instance: object, eventName: string) => void;
    };
  };
};

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-js';
const GOOGLE_MAPS_CALLBACK_NAME = '__evimoGoogleMapsReady';
export const GOOGLE_MAPS_MISSING_API_KEY_ERROR =
  'Missing VITE_PUBLIC_GOOGLE_MAPS_API_KEY';

let googleMapsPromise: Promise<GoogleMapsNamespace> | null = null;

type GoogleMapsWindow = Window & {
  google?: GoogleMapsNamespace;
  [GOOGLE_MAPS_CALLBACK_NAME]?: () => void;
};

async function resolveGoogleMapsNamespace(
  browserWindow: Window & { google?: GoogleMapsNamespace },
): Promise<GoogleMapsNamespace> {
  const googleMaps = browserWindow.google;

  if (!googleMaps) {
    throw new Error('Google Maps loaded without exposing the API');
  }

  const importLibrary = googleMaps.maps.importLibrary?.bind(googleMaps.maps);

  if (!importLibrary) {
    if (!googleMaps.maps.Map) {
      throw new Error('Google Maps Map library is unavailable');
    }

    return googleMaps;
  }

  let mapsLibrary: {
    Map?: GoogleMapsNamespace['maps']['Map'];
  } | null = null;

  try {
    mapsLibrary = (await importLibrary('maps')) as {
      Map?: GoogleMapsNamespace['maps']['Map'];
    };
  } catch {
    if (googleMaps.maps.Map) {
      return googleMaps;
    }
  }

  let markerLibrary: {
    AdvancedMarkerElement?: NonNullable<
      GoogleMapsNamespace['maps']['marker']
    >['AdvancedMarkerElement'];
  } | null = null;
  try {
    markerLibrary = (await importLibrary('marker')) as {
      AdvancedMarkerElement?: NonNullable<
        GoogleMapsNamespace['maps']['marker']
      >['AdvancedMarkerElement'];
    };
  } catch {
    markerLibrary = null;
  }

  let placesLibrary: {
    AutocompleteService?: GoogleMapsNamespace['maps']['places']['AutocompleteService'];
  } | null = null;

  try {
    placesLibrary = (await importLibrary('places')) as {
      AutocompleteService?: GoogleMapsNamespace['maps']['places']['AutocompleteService'];
    };
  } catch {
    placesLibrary = null;
  }

  if (!mapsLibrary?.Map && !googleMaps.maps.Map) {
    throw new Error('Google Maps Map library is unavailable');
  }

  return {
    ...googleMaps,
    maps: {
      ...googleMaps.maps,
      Map: mapsLibrary?.Map ?? googleMaps.maps.Map,
      marker: markerLibrary?.AdvancedMarkerElement
        ? {
            ...(googleMaps.maps.marker ?? {}),
            AdvancedMarkerElement: markerLibrary.AdvancedMarkerElement,
          }
        : googleMaps.maps.marker,
      places: placesLibrary?.AutocompleteService
        ? {
            ...(googleMaps.maps.places ?? {}),
            AutocompleteService: placesLibrary.AutocompleteService,
          }
        : googleMaps.maps.places,
    },
  };
}

function getGoogleMapsApiKey(): string {
  return import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY?.trim() ?? '';
}

export function getGoogleMapsMapId(): string {
  return import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_MAP_ID?.trim() ?? '';
}

export function withGoogleMapsMapId<T extends GoogleMapConstructorOptions>(
  options: T,
): T {
  const mapId = getGoogleMapsMapId();

  if (!mapId) {
    return options;
  }

  return {
    ...options,
    mapId,
  };
}

export function canUseGoogleMapsAdvancedMarkers(): boolean {
  return !!getGoogleMapsMapId();
}

export function isGoogleMapsMissingApiKeyError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message === GOOGLE_MAPS_MISSING_API_KEY_ERROR
  );
}

export function clearGoogleMapsInstanceListeners(
  instance: object | null,
): void {
  if (!instance || typeof window === 'undefined') {
    return;
  }

  const browserWindow = window as Window & { google?: GoogleMapsNamespace };
  browserWindow.google?.maps.event.clearInstanceListeners(instance);
}

export function createGoogleMapsMarker(
  googleMaps: GoogleMapsNamespace,
  options: GoogleMarkerConstructorOptions,
): GoogleMarkerInstance | GoogleAdvancedMarkerInstance {
  const { icon, title, ...rest } = options;
  const advancedMarkerConstructor =
    googleMaps.maps.marker?.AdvancedMarkerElement;

  if (advancedMarkerConstructor && canUseGoogleMapsAdvancedMarkers()) {
    let content: HTMLElement | undefined;

    if (icon) {
      content = document.createElement('img');
      (content as HTMLImageElement).src = icon;
      (content as HTMLImageElement).alt = title || '';

      // Set default size for custom icons if not specified
      content.style.width = '32px';
      content.style.height = '32px';
    }

    return new advancedMarkerConstructor({
      ...rest,
      title,
      content,
    });
  }

  return new googleMaps.maps.Marker(options);
}

export function readGoogleMapsMarkerPosition(
  marker: GoogleMarkerInstance | GoogleAdvancedMarkerInstance,
): GoogleLatLngLiteral | null {
  if ('getPosition' in marker && typeof marker.getPosition === 'function') {
    const position = marker.getPosition();

    if (!position) {
      return null;
    }

    return {
      lat: position.lat(),
      lng: position.lng(),
    };
  }

  if ('position' in marker && marker.position) {
    const position = marker.position;

    if (
      typeof position.lat === 'function' &&
      typeof position.lng === 'function'
    ) {
      return {
        lat: position.lat(),
        lng: position.lng(),
      };
    }

    // position is GoogleLatLngLiteral with number properties
    return {
      lat: position.lat as number,
      lng: position.lng as number,
    };
  }

  return null;
}

export function loadGoogleMapsScript(): Promise<GoogleMapsNamespace> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('Google Maps can only load in the browser'),
    );
  }

  const browserWindow = window as GoogleMapsWindow;

  if (browserWindow.google?.maps?.Map) {
    return resolveGoogleMapsNamespace(browserWindow);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  const apiKey = getGoogleMapsApiKey();

  if (!apiKey) {
    return Promise.reject(new Error(GOOGLE_MAPS_MISSING_API_KEY_ERROR));
  }

  googleMapsPromise = new Promise<GoogleMapsNamespace>((resolve, reject) => {
    const rejectAndReset = (error: Error) => {
      delete browserWindow[GOOGLE_MAPS_CALLBACK_NAME];
      googleMapsPromise = null;
      reject(error);
    };

    const resolveWhenReady = () => {
      void resolveGoogleMapsNamespace(browserWindow)
        .then((googleMaps) => {
          delete browserWindow[GOOGLE_MAPS_CALLBACK_NAME];
          resolve(googleMaps);
        })
        .catch((error: unknown) => {
          rejectAndReset(
            error instanceof Error
              ? error
              : new Error('Failed to initialize Google Maps'),
          );
        });
    };

    browserWindow[GOOGLE_MAPS_CALLBACK_NAME] = () => {
      resolveWhenReady();
    };

    const existingScript = document.getElementById(
      GOOGLE_MAPS_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existingScript) {
      if (browserWindow.google) {
        resolveWhenReady();
        return;
      }

      existingScript.addEventListener('error', () => {
        rejectAndReset(new Error('Failed to load Google Maps script'));
      });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker,geometry&loading=async&callback=${GOOGLE_MAPS_CALLBACK_NAME}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      rejectAndReset(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });

  return googleMapsPromise;
}
